import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';

interface ToastMessage {
  id: number;
  message: string;
  type: 'warning' | 'error';
}

interface ProtectionToastProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center';
  duration?: number;
}

let toastId = 0;
const listeners: Set<(toast: ToastMessage) => void> = new Set();

export function showProtectionToast(message: string, type: 'warning' | 'error' = 'warning') {
  const toast: ToastMessage = {
    id: ++toastId,
    message,
    type,
  };
  listeners.forEach(listener => listener(toast));
}

export function ProtectionToast({ 
  position = 'top-center', 
  duration = 3000 
}: ProtectionToastProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  useEffect(() => {
    const handleNewToast = (toast: ToastMessage) => {
      setToasts(prev => [...prev, toast]);
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, duration);
    };

    listeners.add(handleNewToast);
    return () => {
      listeners.delete(handleNewToast);
    };
  }, [duration]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
  };

  if (toasts.length === 0) return null;

  return (
    <div className={`fixed z-50 flex flex-col gap-2 ${positionClasses[position]}`}>
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`
            flex items-center gap-3 px-4 py-3 rounded shadow-lg
            backdrop-blur-sm bg-white/95
            border-l-4 animate-in slide-in-from-top-2 fade-in duration-200
            ${toast.type === 'error' ? 'border-l-[#e74c3c]' : 'border-l-[#f39c12]'}
          `}
          style={{
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <AlertTriangle 
            className={`w-5 h-5 flex-shrink-0 ${
              toast.type === 'error' ? 'text-[#e74c3c]' : 'text-[#f39c12]'
            }`} 
          />
          <p className="text-sm text-[#2c3e50] font-medium whitespace-nowrap">
            {toast.message}
          </p>
          <button
            onClick={() => removeToast(toast.id)}
            className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      ))}
    </div>
  );
}
